// Puck out of play — corner-stall and rink-escape detection.
//
// Owns:   the rule that whistles play dead when the puck has either
//         (a) somehow escaped the rink boundary (defensive safety net;
//             shouldn't happen with solid boards but does no harm), or
//         (b) gone unattended in a corner for `stallTicksRequired`
//             ticks at low speed — the "battle stalls" arcade pacing
//             rule that keeps the game from drifting on a dead puck.
// Reads:  state.puck, state.players (proximity), tuning.rink, tuning.puck.
// Mutates: state.rules.puckOutOfPlay (cross-tick stall counter), and
//          (via enterStoppage) state.puck / state.clock / state.rules.
// Emits:  Stoppage(reason="PuckOutOfPlay").
//
// The escape path is a one-tick whistle; the stall path requires the
// puck to be slow, in a corner, with no skater within
// `stallNoSkaterRadius` for the entire 3-second window. Any change to
// any of those resets the counter back to 0 — the rule never fires on
// pucks that are being actively contested.
//
// Zero-allocation past the lazy init of the stall counter object.

import { nearestFaceoffSpot } from "../state/faceoff-spots.js";
import { tuning } from "../state/tuning.js";
import type { MatchState } from "../state/types.js";
import { enterStoppage } from "./stoppage.js";

const STALL_MAX_SPEED = 0.3;
// Corner zone — past these absolute bounds the puck is "stuck on the
// boards" rather than in normal play. End-zone dots sit at |x|=20,
// |y|=7; we go further (|x|>22, |y|>12) so the trigger never fires from
// a stalled-near-the-dot puck.
const STALL_CORNER_X = 22;
const STALL_CORNER_Y = 12;
// 3 seconds at 60Hz. Picked to be longer than a typical scrum but
// short enough to keep CPU-vs-CPU pacing honest.
const STALL_TICKS_REQUIRED = 180;
const STALL_NO_SKATER_RADIUS = 2.5;
// Margin past the rink boundary that counts as "escaped." The rink
// solver should keep the puck inside, but if anything slips through we
// reset the puck cleanly via a Stoppage.
const ESCAPE_MARGIN = 0.5;

export function tickPuckOutOfPlay(state: MatchState): void {
  if (state.clock.phase !== "Play" && state.clock.phase !== "Overtime") return;

  // (a) Escape — defensive net. Snap to the nearest dot if the puck
  //     has somehow left the rink.
  const halfL = tuning.rink.length / 2;
  const halfW = tuning.rink.width / 2;
  if (
    Math.abs(state.puck.pos.x) > halfL + ESCAPE_MARGIN ||
    Math.abs(state.puck.pos.y) > halfW + ESCAPE_MARGIN
  ) {
    state.rules.puckOutOfPlay = undefined;
    const spot = nearestFaceoffSpot(state.puck.pos.x, state.puck.pos.y, null);
    enterStoppage(state, spot, "PuckOutOfPlay");
    return;
  }

  // (b) Corner stall. All conditions reset the counter on failure so
  //     the rule only fires when nothing has changed for 3 full seconds.
  if (state.puck.mode === "Controlled") {
    state.rules.puckOutOfPlay = undefined;
    return;
  }
  const vx = state.puck.vel.x;
  const vy = state.puck.vel.y;
  if (vx * vx + vy * vy >= STALL_MAX_SPEED * STALL_MAX_SPEED) {
    state.rules.puckOutOfPlay = undefined;
    return;
  }
  if (Math.abs(state.puck.pos.x) < STALL_CORNER_X || Math.abs(state.puck.pos.y) < STALL_CORNER_Y) {
    state.rules.puckOutOfPlay = undefined;
    return;
  }
  for (const p of state.players) {
    if (p.benched) continue;
    const dx = p.pos.x - state.puck.pos.x;
    const dy = p.pos.y - state.puck.pos.y;
    if (dx * dx + dy * dy < STALL_NO_SKATER_RADIUS * STALL_NO_SKATER_RADIUS) {
      state.rules.puckOutOfPlay = undefined;
      return;
    }
  }

  // Conditions hold — increment the counter. Lazy init so the per-tick
  // hash contribution stays zero for matches that never stall.
  let stall = state.rules.puckOutOfPlay;
  if (stall === undefined) {
    stall = { ticks: 0 };
    state.rules.puckOutOfPlay = stall;
  }
  stall.ticks += 1;
  if (stall.ticks >= STALL_TICKS_REQUIRED) {
    state.rules.puckOutOfPlay = undefined;
    const spot = nearestFaceoffSpot(state.puck.pos.x, state.puck.pos.y, null);
    enterStoppage(state, spot, "PuckOutOfPlay");
  }
}

export {
  STALL_MAX_SPEED,
  STALL_CORNER_X,
  STALL_CORNER_Y,
  STALL_TICKS_REQUIRED,
  STALL_NO_SKATER_RADIUS,
};
